/**
 * Power of Attorney, classic look: the third mode of the IMT Admin Dashboard
 * (after Employee Management and System Data). Table From / To / Roles /
 * Valid from / Valid until / Status, a person and status filter, and the
 * add, edit and revoke pop-ups. Logic and texts: usePowerOfAttorney.
 *
 * IMT copy of HMS 2e421ecb: same hook and texts, drawn with IMT's own
 * HMSTable, HMSModal and the dashboard's form-group fields (IMT has no
 * HMSConfirm or ui/fields yet). Rows come in the server's order, newest
 * start date first.
 */
import React from 'react';
import HMSButton from '../ui/HMSButton';
import HMSModal from '../ui/HMSModal';
import HMSTable from '../ui/HMSTable';
import { formatHrDate } from './useEmployeeCrmPartner';
import usePowerOfAttorney, { STATUS_FILTERS, rolesText, validUntilText } from './usePowerOfAttorney';
import './hr.css';

const COLUMNS = [
  { key: 'grantor', label: 'From' },
  { key: 'grantee', label: 'To' },
  { key: 'roles', label: 'Roles' },
  { key: 'valid_from', label: 'Valid from' },
  { key: 'valid_to', label: 'Valid until' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Actions', align: 'right' },
];

export function SelectField({ id, label, required = false, placeholder, value, onChange, options }) {
  return (
    <div className="form-group">
      <label htmlFor={id}>{label}{required ? ' *' : ''}</label>
      <select id={id} className="form-input" value={value} onChange={onChange} required={required}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function DateField({ id, label, value, onChange }) {
  return (
    <div className="form-group">
      <label htmlFor={id}>{label} *</label>
      <input id={id} type="date" className="form-input" required value={value} onChange={onChange} />
    </div>
  );
}

export function PowerOfAttorneyForm({ poa }) {
  const { form, setField, toggleRole, formPeople, formRoles, formError } = poa;
  return (
    <div className="hr-attorney-form">
      <div className="hr-attorney-form-grid">
        <SelectField
          id="attorney-grantor"
          label="From who"
          required
          placeholder="Choose a person"
          value={form.grantor_id}
          onChange={(e) => setField('grantor_id', e.target.value)}
          options={formPeople}
        />
        <SelectField
          id="attorney-grantee"
          label="To whom"
          required
          placeholder="Choose a person"
          value={form.grantee_id}
          onChange={(e) => setField('grantee_id', e.target.value)}
          options={formPeople}
        />
      </div>

      <fieldset className="hr-attorney-roles">
        <legend>Which roles *</legend>
        {formRoles.length === 0 ? (
          <span className="hr-attorney-muted">No roles yet. Add them in System Data, Power of Attorney Roles.</span>
        ) : formRoles.map((role) => (
          <label key={role.id} className="hr-attorney-check">
            <input
              type="checkbox"
              checked={form.role_ids.includes(role.id)}
              onChange={(e) => toggleRole(role.id, e.target.checked)}
            />
            <span>{role.name}{role.is_active ? '' : ' (switched off)'}</span>
          </label>
        ))}
      </fieldset>

      <div className="hr-attorney-form-grid">
        <DateField
          id="attorney-valid-from"
          label="Valid from"
          value={form.valid_from}
          onChange={(e) => setField('valid_from', e.target.value)}
        />
        {form.until_revoked ? (
          <div />
        ) : (
          <DateField
            id="attorney-valid-to"
            label="Valid until"
            value={form.valid_to}
            onChange={(e) => setField('valid_to', e.target.value)}
          />
        )}
      </div>
      <label className="hr-attorney-check">
        <input
          type="checkbox"
          checked={form.until_revoked}
          onChange={(e) => setField('until_revoked', e.target.checked)}
        />
        <span>Valid until revoked (no end date)</span>
      </label>

      <div className="form-group">
        <label htmlFor="attorney-notes">Notes</label>
        <textarea
          id="attorney-notes"
          className="form-input"
          rows={3}
          value={form.notes}
          onChange={(e) => setField('notes', e.target.value)}
        />
      </div>

      {formError ? <p className="hr-attorney-error" role="alert">{formError}</p> : null}
    </div>
  );
}

export default function PowerOfAttorneyManagement({ employees }) {
  const poa = usePowerOfAttorney(employees);

  const renderCell = (r, column) => {
    switch (column.key) {
      case 'grantor':
        return r.grantor.name;
      case 'grantee':
        return r.grantee.name;
      case 'roles':
        return rolesText(r);
      case 'valid_from':
        return formatHrDate(r.valid_from);
      case 'valid_to':
        return validUntilText(r);
      case 'status':
        return <span className={`hr-attorney-status hr-attorney-status--${r.status}`}>{r.status_label}</span>;
      case 'actions':
        return r.status === 'revoked' ? null : (
          <div className="hr-attorney-row-actions">
            <HMSButton variant="ghost" size="compact" onClick={() => poa.openEdit(r)}>Edit</HMSButton>
            <HMSButton variant="dangerGhost" size="compact" onClick={() => poa.askRevoke(r)}>Revoke</HMSButton>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="hr-attorney">
      <div className="hr-attorney-toolbar">
        <div className="hr-attorney-filters">
          <SelectField
            id="attorney-filter-person"
            label="Person"
            placeholder="Everyone"
            value={poa.personFilter}
            onChange={(e) => poa.setPersonFilter(e.target.value)}
            options={poa.filterPeople}
          />
          <SelectField
            id="attorney-filter-status"
            label="Status"
            placeholder="All statuses"
            value={poa.statusFilter}
            onChange={(e) => poa.setStatusFilter(e.target.value)}
            options={STATUS_FILTERS}
          />
          {poa.hasFilters ? (
            <HMSButton variant="ghost" size="compact" onClick={poa.clearFilters}>Clear filters</HMSButton>
          ) : null}
        </div>
        <HMSButton onClick={poa.openNew}>Add Power of Attorney</HMSButton>
      </div>

      {poa.loadError ? (
        <p className="hr-attorney-error" role="alert">
          {poa.loadError}{' '}
          <HMSButton variant="ghost" size="compact" onClick={poa.reload}>Try again</HMSButton>
        </p>
      ) : null}

      {poa.loading ? (
        <p className="hr-attorney-muted" role="status">Loading powers of attorney...</p>
      ) : poa.rows.length === 0 ? (
        <p className="hr-attorney-muted">
          {poa.hasFilters
            ? 'No power of attorney matches these filters. Choose another person or status.'
            : 'No powers of attorney yet. Add the first one with "Add Power of Attorney".'}
        </p>
      ) : (
        <HMSTable columns={COLUMNS} rows={poa.rows} rowKey={(r) => r.id} renderCell={renderCell} />
      )}

      <HMSModal
        isOpen={Boolean(poa.editing)}
        onClose={poa.closeForm}
        title={poa.editing === 'new' ? 'Add Power of Attorney' : 'Edit Power of Attorney'}
        width={640}
        showClose
        busy={poa.saving}
        closeOnOverlay={false}
        footer={(
          <>
            <HMSButton variant="secondary" onClick={poa.closeForm} disabled={poa.saving}>Cancel</HMSButton>
            <HMSButton onClick={poa.save} disabled={poa.saving}>
              {poa.saving ? 'Saving...' : 'Save'}
            </HMSButton>
          </>
        )}
      >
        {poa.editing ? <PowerOfAttorneyForm poa={poa} /> : null}
      </HMSModal>

      <HMSModal
        isOpen={Boolean(poa.revoking)}
        onClose={poa.cancelRevoke}
        title="Revoke this power of attorney?"
        width={480}
        busy={poa.revokeBusy}
        footer={(
          <>
            <HMSButton variant="secondary" onClick={poa.cancelRevoke} disabled={poa.revokeBusy}>Cancel</HMSButton>
            <HMSButton variant="danger" onClick={poa.confirmRevoke} disabled={poa.revokeBusy}>
              {poa.revokeBusy ? 'Revoking...' : 'Revoke'}
            </HMSButton>
          </>
        )}
      >
        {poa.revoking ? (
          <p className="hr-attorney-muted">
            {`${poa.revoking.grantee.name} will no longer hold the power of attorney from ${poa.revoking.grantor.name} (${rolesText(poa.revoking)}). The record stays in the list as Revoked.`}
          </p>
        ) : null}
      </HMSModal>
    </div>
  );
}
